<template>
  <!-- For API routes, render nothing to prevent redirects -->
  <div v-if="isApiRoute" style="display: none" />

  <!-- For normal routes, render the error page -->
  <NuxtLayout v-else>
    <div v-if="resolvedPage">
      <div v-if="errorPageHasLoadingError || !page" :class="$style.error">
        Something went wrong
        <pre v-if="isPreviewEnabled">{{ error }}</pre>
        <pre v-if="isPreviewEnabled">{{
          String(errorPageHasLoadingError)
        }}</pre>
      </div>
      <MappedComponent v-else :item="resolvedPage" />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { NuxtError } from 'nuxt/app';
import { setResponseStatus } from 'nuxt/app';
import type { EntryWithLinkData, IContentPage } from '~~/lib/ContentfulService';
import { INJECTED_COLOR_MODE } from '~~/lib/constants';

interface Props {
  error: NuxtError;
}

const props = defineProps<Props>();

const locale = useLocale();
const { $globalPageSettings, $colorMode } = useNuxtApp();

const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();

// Check if this is an API route
const isApiRoute = computed(() => {
  const requestEvent = useRequestEvent();
  return requestEvent && requestEvent.path.startsWith('/api/');
});

const { data: page, error: errorPageHasLoadingError } =
  await useAsyncData<EntryWithLinkData<IContentPage> | null>(
    async (nuxtApp) => {
      const requestEvent = useRequestEvent(nuxtApp);

      // Skip error page handling for API routes
      if (requestEvent && requestEvent.path.startsWith('/api/')) {
        // For API routes, don't fetch error page, just return null
        return null;
      }

      if (requestEvent) {
        setResponseStatus(
          requestEvent,
          props.error.statusCode,
          props.error.statusMessage
        );
      }

      return $fetch<EntryWithLinkData<IContentPage>>(
        '/api/contentful/errorPage',
        {
          ...DEFAULT_FETCH_OPTIONS,
          query: { locale: locale.value, statusCode: props.error.statusCode }
        }
      );
    },
    {
      watch: [() => props.error, locale]
    }
  );

const resolvedPage = computed(() => {
  if (!page.value) {
    return null;
  }

  // The error page itself must never throw on an unreleased reference (DBP-4306).
  return resolveEntry(page.value, true);
});
const colorMode = computed(
  () => resolvedPage.value?.fields?.pageTheme ?? 'dark'
);

watch(
  () => resolvedPage.value?.fields?.pageTheme,
  () => {
    $colorMode.value = resolvedPage.value?.fields?.pageTheme ?? 'dark';
    provide(INJECTED_COLOR_MODE, readonly(colorMode));
  },
  { immediate: true }
);

if (resolvedPage.value) {
  const companyName = $globalPageSettings.value?.companyName ?? '';
  useMetadata(resolvedPage.value, companyName);
}
</script>

<style module>
.error {
  color: #fff;
}
</style>
